import React from 'react'
import FormField from '@/components/common/FormField'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { errorTypes } from '@/utility/validation'
import { useFormContext } from 'react-hook-form'
import type { OnboardTabProps } from './onboard.types'

import { CheckCircle2 } from 'lucide-react'

const fetchBankDetailsByIfsc = async (ifsc: string) => {
    if (!ifsc || ifsc.trim().length !== 11) return null;
    try {
        const response = await fetch(`https://ifsc.razorpay.com/${ifsc.trim().toUpperCase()}`);
        if (response.ok) {
            const data = await response.json();
            return {
                bankName: data.BANK || "",
                branchAddress: data.ADDRESS || `${data.BRANCH || ""}, ${data.CITY || ""}`.trim(),
                isValid: true,
            };
        }
        return { isValid: false };
    } catch (e) {
        console.error("Error fetching IFSC details:", e);
        return null;
    }
};

const OnboardBankDetails: React.FC<OnboardTabProps> = ({ isLoading, handleNextStep }) => {
    const methods = useFormContext()
    const { register, watch, setValue, setError, clearErrors, formState: { errors } } = methods
    const ifscCode = watch('ifscCode')
    const [ifscVerified, setIfscVerified] = React.useState<boolean>(false)
    const [verifiedBankInfo, setVerifiedBankInfo] = React.useState<string>("")
    const [isVerifyingIfsc, setIsVerifyingIfsc] = React.useState<boolean>(false)

    React.useEffect(() => {
        const trimmed = ifscCode ? ifscCode.trim().toUpperCase() : ""
        if (trimmed.length === 11) {
            const getBankDetails = async () => {
                setIsVerifyingIfsc(true)
                const result = await fetchBankDetailsByIfsc(trimmed);
                setIsVerifyingIfsc(false)
                if (result && result.isValid) {
                    setIfscVerified(true)
                    setVerifiedBankInfo(result.bankName || "Valid IFSC")
                    if (result.bankName) {
                        setValue('bankName', result.bankName, { shouldValidate: true, shouldDirty: true });
                    }
                    if (result.branchAddress) {
                        setValue('branchAddress', result.branchAddress, { shouldValidate: true, shouldDirty: true });
                    }
                    clearErrors('ifscCode');
                } else if (result && !result.isValid) {
                    setIfscVerified(false)
                    setVerifiedBankInfo("")
                    setError('ifscCode', {
                        type: 'manual',
                        message: 'Invalid IFSC Code. Bank not found.',
                    });
                }
            };
            getBankDetails();
        } else {
            setIfscVerified(false)
            setVerifiedBankInfo("")
        }
    }, [ifscCode, setValue, setError, clearErrors]);

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <FormField
                        {...register("ifscCode", {
                            ...errorTypes.required('IFSC Code'),
                            ...errorTypes?.IFSC,
                        })}
                        value={ifscCode || ""}
                        onChange={(e) => {
                            const raw = (e.target.value || "").toUpperCase();
                            const clean = raw.replace(/[^A-Z0-9]/g, "").slice(0, 11);
                            setValue('ifscCode', clean, { shouldValidate: true, shouldDirty: true });
                        }}
                        error={errors.ifscCode?.message as string}
                        label="IFSC (Auto Fetch Bank & Branch)"
                        placeholder="e.g. SBIN0000691"
                        required
                        maxLength={11}
                    />
                    {isVerifyingIfsc && (
                        <p className="text-xs text-blue-600 mt-1">Verifying IFSC Code...</p>
                    )}
                    {!errors.ifscCode && ifscVerified && (
                        <p className="text-xs font-medium text-emerald-600 flex items-center gap-1 mt-1">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            <span>IFSC Code is verified ({verifiedBankInfo})</span>
                        </p>
                    )}
                </div>
                <FormField
                    {...register('bankName', {
                        ...errorTypes.required('Bank Name'),
                        ...errorTypes.name,
                    })}
                    error={errors.bankName?.message as string}
                    label="Bank Name"
                    placeholder="Enter bank name"
                    required
                />
                <FormField
                    {...register('accountNumber', {
                        ...errorTypes.required('Account Number'),
                        ...errorTypes?.accountNumber,
                    })}
                    error={errors.accountNumber?.message as string}
                    label="Account Number"
                    placeholder="Enter account number"
                    required
                    maxLength={18}
                />
                <FormField
                    {...register('accountHolderName', {
                        ...errorTypes.required('Account Holder Name'),
                        ...errorTypes.name,
                    })}
                    error={errors.accountHolderName?.message as string}
                    label="Account Holder Name"
                    placeholder="Enter account holder name"
                    required
                />
                <FormField
                    {...register("branchAddress", {
                        ...errorTypes.required('Branch Address'),
                        ...errorTypes?.address,
                    })}
                    error={errors.branchAddress?.message as string}
                    label="Branch Address"
                    placeholder="Enter branch address"
                    rows={3}
                    required
                />
            </div>
            <div className="flex items-center justify-end mt-6">
                <Button
                    type="button"
                    onClick={() => handleNextStep("bank_details", 'tab7', ['bankName', 'accountNumber', 'accountHolderName', 'ifscCode', 'branchAddress'])}
                >
                    {isLoading ? <Spinner /> : 'Save and Continue'}
                </Button>
            </div>
        </>
    )
}

export default OnboardBankDetails
